/**
 * Route Utilities - Helper functions for permission-based route filtering
 */

/**
 * Check if a user has access to a specific route based on permission requirements
 * 
 * @param {Object|Array|null} requiredPermission - Permission requirement(s)
 * @param {Function} canCheck - The can checking function from useCan hook
 * @returns {boolean} - True if user has access
 */
export const hasRouteAccess = (requiredPermission, canCheck, requiredModule = null, moduleAccess = null) => {
  if (requiredModule && (!moduleAccess || !moduleAccess.isEnabled(requiredModule))) {
    return false;
  }

  // No permission required = public route (accessible to all authenticated users)
  if (!requiredPermission) {
    return true;
  }

  // Single permission check
  if (!Array.isArray(requiredPermission)) {
    const { section, level = 'read', action } = requiredPermission;
    return action 
      ? canCheck.section(section, { action })
      : canCheck.section(section, { level });
  }

  // Multiple permissions - user needs ANY of them
  return canCheck.any(requiredPermission.map(p => ({ 
    section: p.section, 
    level: p.level || 'read' 
  })));
};

/**
 * Filter routes based on user permissions
 * 
 * @param {Array} routes - Array of route configurations
 * @param {Function} canCheck - The can checking function from useCan hook
 * @returns {Array} - Filtered array of accessible routes
 */
export const filterAccessibleRoutes = (routes, canCheck, moduleAccess = null) => {
  return routes.filter(route => hasRouteAccess(route.permission, canCheck, route.module, moduleAccess));
};

/**
 * Get accessible navigation items based on routes
 * Useful for filtering drawer/menu items
 * 
 * @param {Array} navItems - Navigation items with route paths
 * @param {Array} routes - Array of route configurations
 * @param {Function} canCheck - The can checking function from useCan hook
 * @returns {Array} - Filtered navigation items
 */
export const filterAccessibleNavItems = (navItems, routes, canCheck, moduleAccess = null) => {
  const filteredItems = navItems.filter(item => {
    if (item.divider) return true;
    
    // If item has no route, it might be a section header or parent item
    if (!item.route) return true;
    
    const menuPath = item.route.replace(/^\//, '');
    const matchingRoute = routes.find(route => route.path === menuPath)
      || routes.find(route => route.path.split('/')[0] === menuPath.split('/')[0]);
    return matchingRoute
      ? hasRouteAccess(matchingRoute.permission, canCheck, matchingRoute.module, moduleAccess)
      : false;
  });

  return filteredItems.filter((item, index) => {
    if (!item.divider) return true;
    return index > 0
      && index < filteredItems.length - 1
      && !filteredItems[index - 1].divider
      && !filteredItems[index + 1].divider;
  });
};

/**
 * Check if a specific path is accessible
 * 
 * @param {string} path - Route path to check
 * @param {Array} routes - Array of route configurations
 * @param {Function} canCheck - The can checking function from useCan hook
 * @returns {boolean} - True if path is accessible
 */
export const isPathAccessible = (path, routes, canCheck, moduleAccess = null) => {
  // Remove leading slash and params for matching
  const cleanPath = path.replace(/^\//, '').split('?')[0].split('/')[0];
  
  const matchingRoute = routes.find(route => {
    const routePath = route.path.split('/')[0];
    return routePath === cleanPath;
  });

  if (!matchingRoute) {
    // Route doesn't exist in configuration
    return false;
  }

  return hasRouteAccess(matchingRoute.permission, canCheck, matchingRoute.module, moduleAccess);
};
